import { getActivityStatus } from '@flashka/shared/sync-client/study-groups';
import { Avatar, Tooltip } from '@flashka/shared/ui';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { cn } from '@/lib/utils';

// Legacy size names → DS Avatar sizes (same pixel geometry: 20/28/32/36/40).
const sizeMap = {
  sm: '2xs',
  md: 'xs',
  base: 's',
  lg: 'm',
  xl: 'l',
} as const;

export const MemberAvatar = ({
  name,
  avatarName,
  avatar,
  size = 'md',
  className,
  border = false,
  tooltipSide = 'bottom',
  lastActive,
  handle,
  userId,
  showTooltip = true,
  linkTo,
  linkLabel,
  linkOnClick,
}: {
  name?: string | null;
  /**
   * Overrides the name used for the initials fallback while `name` still
   * drives the tooltip/label. Lets the leaderboard's viewer row show the
   * real account initials ("MP") under a "You" tooltip. Defaults to `name`.
   */
  avatarName?: string | null;
  avatar?: string | null;
  size?: keyof typeof sizeMap;
  className?: string;
  border?: boolean;
  tooltipSide?: 'top' | 'bottom' | 'left' | 'right';
  lastActive?: string | null;
  /** When set, the avatar becomes a link to `/profile/@handle`. Callers
   *  that don't have a resolvable handle (e.g. legacy leaderboard rows)
   *  simply omit it and the avatar stays non-interactive. */
  handle?: string | null;
  /** Stable user id — preferred over `handle` when the caller has
   *  it. The profile route accepts a raw user_id locator, and using it
   *  avoids the collision risk of a handle scraped from a display string
   *  (e.g. `@emailPrefix`) that another real user might currently own. */
  userId?: string | null;
  /** Set to `false` when the surrounding row ALREADY renders the
   *  @username in text (followers/following, notification cards) — the
   *  name-in-tooltip is redundant there. Defaults to `true` for the
   *  member-stack / leaderboard case where the name only exists on
   *  hover. */
  showTooltip?: boolean;
  /**
   * Overrides where the avatar links, for rows that are not people. The
   * "Top universities" leaderboard row uses it to send a click to Find Mates
   * pre-filtered by that school instead of to a `/profile/uni:<id>` that
   * cannot exist. Wins over `userId` / `handle`.
   */
  linkTo?: string;
  /** `aria-label` for that link. Required whenever `linkTo` is set. */
  linkLabel?: string;
  /** Fired on the link click, before navigation (analytics). */
  linkOnClick?: () => void;
}) => {
  const { t } = useTranslation();
  const activity = getActivityStatus(lastActive, t);

  // Wrapped in a <span> so Radix `TooltipTrigger asChild` (Slot) has a real
  // DOM node to attach its ref + hover/focus listeners to — the shared Avatar
  // renders a View that forwards neither, so a bare <Avatar> silently loses
  // the tooltip. The span is display:contents-free `inline-flex` so it doesn't
  // change layout around the circle.
  // Prefer a userId locator (stable, immune to handle drift) over a handle
  // scraped from a display string. Fall back to `@handle` when only a handle
  // is known (own-profile shortcuts, curated links).
  const profilePath =
    linkTo ?? (userId ? `/profile/${userId}` : handle ? `/profile/@${handle}` : null);
  const clickable = !!profilePath;
  const circle = (
    <span className="inline-flex">
      <Avatar
        size={sizeMap[size]}
        src={avatar ?? undefined}
        // "?" keeps the legacy fallback glyph for members with no name yet.
        name={avatarName || name || '?'}
        className={cn(
          border && 'border-2 border-white dark:border-secondary-50',
          // Clickable member avatars (member lists, leaderboard rows, deck
          // authorship, notification cards) get a plain pointer cursor —
          // nothing else. The previous purple ring + scale-105 was too loud
          // and doubled up with the tooltip/name click affordance.
          clickable && 'cursor-pointer',
          className,
        )}
      />
    </span>
  );

  // When we have a handle, hand the click off to a Link. Stop tooltip
  // clicks from bubbling to any parent row that also navigates —
  // otherwise the row's onClick would fire alongside the profile
  // navigation and blow up the intent.
  const targetable = profilePath ? (
    <Link
      to={profilePath}
      aria-label={linkLabel ?? (name ? `${name} profile` : 'profile')}
      onClick={(e) => {
        e.stopPropagation();
        linkOnClick?.();
      }}
      className="inline-flex">
      {circle}
    </Link>
  ) : (
    circle
  );

  if (!name || !showTooltip) return targetable;

  return (
    <Tooltip
      content={
        <div className="flex flex-col gap-0.5 font-bold">
          <span className="w-full text-center text-xs text-subtitle-800">{name}</span>
          {activity && (
            <span className="flex items-center gap-1.5 text-xs text-body-600">
              <span className={cn('h-2 w-2 flex-none rounded-full', activity.color)} />
              {activity.label}
            </span>
          )}
        </div>
      }
      side={tooltipSide}
      delayMs={20}
      className="rounded-lg px-2 py-1.5">
      {targetable}
    </Tooltip>
  );
};
